/**
 * Home page layout preferences, persisted per user in a cookie so the same
 * browser always shows the same home page.
 *
 * Stored shape is compact ({o, h, f}) to stay well under the 4KB cookie limit.
 */

export type WidgetFilterValues = Record<string, unknown>;

export interface HomePrefs {
  /** Widget ids in display order (may include ids the user can no longer see). */
  order: string[];
  /** Widget ids the user has hidden. */
  hidden: string[];
  /** Per-widget filter overrides on top of the role defaults. */
  filters: Record<string, WidgetFilterValues>;
}

export const EMPTY_PREFS: HomePrefs = { order: [], hidden: [], filters: {} };

const COOKIE_PREFIX = "orion_home_v1_";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const MAX_COOKIE_BYTES = 3800;

/** Short, stable, non-reversible key so the cookie name doesn't expose the uid. */
export const userCookieKey = (uid: string): string => {
  let hash = 5381;
  for (let i = 0; i < uid.length; i += 1) {
    hash = ((hash << 5) + hash + uid.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
};

export const cookieNameFor = (uid: string) => `${COOKIE_PREFIX}${userCookieKey(uid)}`;

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === "string");

export const decodePrefs = (raw: string | null | undefined): HomePrefs => {
  if (!raw) return EMPTY_PREFS;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    const filters =
      parsed?.f && typeof parsed.f === "object" && !Array.isArray(parsed.f) ? parsed.f : {};
    return {
      order: isStringArray(parsed?.o) ? parsed.o : [],
      hidden: isStringArray(parsed?.h) ? parsed.h : [],
      filters,
    };
  } catch {
    return EMPTY_PREFS;
  }
};

export const encodePrefs = (prefs: HomePrefs): string =>
  encodeURIComponent(JSON.stringify({ o: prefs.order, h: prefs.hidden, f: prefs.filters }));

export const readPrefsCookie = (uid: string): HomePrefs => {
  const name = cookieNameFor(uid);
  const match = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`));
  return decodePrefs(match ? match.slice(name.length + 1) : null);
};

export const writePrefsCookie = (uid: string, prefs: HomePrefs): void => {
  const value = encodePrefs(prefs);
  if (value.length > MAX_COOKIE_BYTES) {
    console.warn("Home page preferences are too large to save in a cookie; keeping previous layout.");
    return;
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${cookieNameFor(uid)}=${value}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
};

export const clearPrefsCookie = (uid: string): void => {
  document.cookie = `${cookieNameFor(uid)}=; Max-Age=0; Path=/; SameSite=Lax`;
};

/**
 * Order visible widgets: saved order first (dropping unknown ids), then any
 * widgets not in the saved order at their default position.
 */
export const resolveOrder = (
  availableIdsInDefaultOrder: string[],
  prefs: HomePrefs
): string[] => {
  const available = new Set(availableIdsInDefaultOrder);
  const saved = prefs.order.filter((id) => available.has(id));
  const savedSet = new Set(saved);
  const result = [...saved];
  availableIdsInDefaultOrder.forEach((id, defaultIndex) => {
    if (savedSet.has(id)) return;
    // Insert after the closest preceding default-order neighbour already placed.
    let insertAt = 0;
    for (let i = defaultIndex - 1; i >= 0; i -= 1) {
      const idx = result.indexOf(availableIdsInDefaultOrder[i]);
      if (idx !== -1) {
        insertAt = idx + 1;
        break;
      }
    }
    result.splice(insertAt, 0, id);
  });
  return result;
};

export type MoveDirection = "up" | "down" | "top" | "bottom";

/** Move `id` within the currently visible order and return the new full order. */
export const moveWidget = (visibleOrder: string[], id: string, direction: MoveDirection): string[] => {
  const from = visibleOrder.indexOf(id);
  if (from === -1) return visibleOrder;
  const next = [...visibleOrder];
  next.splice(from, 1);
  const to =
    direction === "top"
      ? 0
      : direction === "bottom"
        ? next.length
        : direction === "up"
          ? Math.max(0, from - 1)
          : Math.min(next.length, from + 1);
  next.splice(to, 0, id);
  return next;
};
